import { getOptjobAsObject, OptJobType, useLatestOptJobStatus } from 'api/optJobs/optJobs.api'
import { useSystem } from 'api/systems/systems.api'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { SubtypeObject } from 'ui/components/NotificationCenter/NotificationCenter.helper'

type LatestSubtypeOptimizationInfoBoxProps = {
  optJobType: OptJobType
}

export default function LatestSubtypeOptimizationInfoBox({
  optJobType,
}: LatestSubtypeOptimizationInfoBoxProps): ReactElement | null {
  const { t } = useTranslation()
  const { systemId, hasAccess } = useAuth()
  const { data: activeSystem } = useSystem({ id: systemId })

  const { data: latestFinishedOptJob } = useLatestOptJobStatus({ type: optJobType, status: 'Finished' })
  const firstLatestFinishedOptJob = getOptjobAsObject(latestFinishedOptJob)

  const showSystemSubtypes = useMemo(() => {
    if (!hasAccess({ submodule: 'optimize_subnet', module: 'optimize' })) {
      return false
    }

    return (activeSystem?.subtypes?.[optJobType]?.length ?? 0) > 0
  }, [activeSystem?.subtypes, hasAccess, optJobType])

  const latestOptimizedSingleSubtype = firstLatestFinishedOptJob?.subtypes?.length === 1

  const subtypeName = useMemo(() => {
    const subtypes = firstLatestFinishedOptJob?.subtypes?.map(
      (subtype: SubtypeObject) => subtype.display_name ?? subtype.name
    )

    if (!subtypes || subtypes.length === 0) {
      return ''
    }

    if (subtypes.length === 1) {
      return subtypes[0]
    }

    if (subtypes.length === 2) {
      return subtypes.join(' & ')
    }

    return `${subtypes.slice(0, -1).join(', ')} & ${subtypes[subtypes.length - 1]}`
  }, [firstLatestFinishedOptJob?.subtypes])

  // Only show if system has subtypes and the latest optimization was for a single subtype
  if (!showSystemSubtypes || !latestOptimizedSingleSubtype || !subtypeName) {
    return null
  }

  return (
    <div className="flex items-center p-1 border border-warning-4 rounded text-xs font-bold text-center h-8">
      {t('Last optimized for {{subtype}}', { subtype: subtypeName })}
    </div>
  )
}
